import { jsx, jsxs } from "react/jsx-runtime";
import { useId, useState } from "react";
import { useTranslation } from "@crehana/i18n";
import { differenceInDays, subDays } from "date-fns";
import { Tab } from "../../Tab/index.js";
import { Tabs } from "../../Tabs/index.js";
import { Calendar } from "../Calendar/index.js";
import { Popper, PopperContent, PopperTrigger } from "../Popper/index.js";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../Select/index.js";
import { cn } from "../utils.js";
import { DateInput } from "./components/DateInput/index.js";
import i18n from "./i18n/index.js";
const AVAILABLE_PERIOD_LENGTHS = [
    7,
    15,
    30,
    60,
    90,
    180,
    365
];
const setToEndOfDay = (date)=>{
    date.setHours(23, 59, 59, 999);
    return date;
};
const tabs = [
    {
        label: 'DATE_PICKER_LAST_TAB',
        value: 'last'
    },
    {
        label: 'DATE_PICKER_SINGLE_TAB',
        value: 'single'
    },
    {
        label: 'DATE_PICKER_RANGE_TAB',
        value: 'range'
    }
];
const DatePickerWithTabs = ({ value, defaultTab, enabledTabs = [
    'last',
    'single',
    'range'
], onChange, className, disabled })=>{
    const { languageCode } = useTranslation();
    if (defaultTab && !enabledTabs.includes(defaultTab)) throw new Error('defaultTab must be one of the enabledTabs');
    const defaultTabIndex = tabs.findIndex(({ value })=>value === defaultTab);
    const fallbackTabIndex = tabs.findIndex(({ value })=>value === enabledTabs[0]);
    const [selectedTab, setSelectedTab] = useState(-1 === defaultTabIndex ? fallbackTabIndex : defaultTabIndex);
    const [selectedDateLabel, setSelectedDateLabel] = useState();
    const id = useId();
    const isPeriodError = value?.from && !value?.to;
    const onTabChange = (value)=>{
        setSelectedTab(value);
        setSelectedDateLabel(void 0);
        onChange?.(void 0);
    };
    const handleSetPeriod = (selected)=>{
        setSelectedDateLabel(selected);
        if (!selected) return;
        let dates = {};
        if ('string' == typeof selected) dates = {
            from: subDays(new Date(), Number(selected)),
            to: setToEndOfDay(new Date())
        };
        else if (selected instanceof Date) dates = {
            from: selected,
            to: setToEndOfDay(selected)
        };
        else {
            const selectedDate = selected;
            dates = {
                from: selectedDate.from,
                to: selectedDate.to ? setToEndOfDay(selectedDate.to) : void 0
            };
        }
        onChange?.({
            tab: tabs[selectedTab].value,
            dates
        });
    };
    const lastPeriods = AVAILABLE_PERIOD_LENGTHS.map((days)=>({
            label: i18n[languageCode].DATE_PICKER_LAST_LABEL.replace('{{count}}', days.toString()),
            value: days.toString()
        }));
    const selectedPeriodMode = tabs[selectedTab].value;
    const handleMaskPeriod = 'last' === selectedPeriodMode ? (selectedValues)=>i18n[languageCode].DATE_PICKER_LAST_LABEL.replace('{{count}}', selectedValues.toString()) : void 0;
    const defaultLastPeriodValue = value?.from ? AVAILABLE_PERIOD_LENGTHS.find((days)=>days === differenceInDays(new Date(), value.from))?.toString() : lastPeriods[0].value;
    const tabContent = {
        last: /*#__PURE__*/ jsxs(Select, {
            disabled: disabled,
            onValueChange: handleSetPeriod,
            defaultValue: defaultLastPeriodValue,
            children: [
                /*#__PURE__*/ jsx(SelectTrigger, {
                    className: "tw-w-28 tw-mb-1",
                    children: /*#__PURE__*/ jsx(SelectValue, {
                        placeholder: lastPeriods[0].label
                    })
                }),
                /*#__PURE__*/ jsx(SelectContent, {
                    className: "tw-w-auto",
                    children: lastPeriods.map(({ label, value })=>/*#__PURE__*/ jsx(SelectItem, {
                            value: value,
                            children: label
                        }, label))
                })
            ]
        }),
        single: /*#__PURE__*/ jsx(Calendar, {
            disabled: disabled,
            id: `${id}-date-picker-single`,
            mode: "single",
            selected: value?.from,
            onSelect: handleSetPeriod,
            defaultMonth: value?.from,
            initialFocus: true,
            className: "tw-shadow-none tw-p-0 tw-pb-1"
        }),
        range: /*#__PURE__*/ jsx(Calendar, {
            disabled: disabled,
            id: `${id}-date-picker-range`,
            mode: "range",
            selected: value,
            defaultMonth: value?.to,
            onSelect: handleSetPeriod,
            initialFocus: true,
            className: "tw-shadow-none tw-p-0 tw-pb-1"
        })
    };
    return /*#__PURE__*/ jsxs(Popper, {
        children: [
            /*#__PURE__*/ jsx(PopperTrigger, {
                children: /*#__PURE__*/ jsx(DateInput, {
                    disabled: disabled,
                    mode: 'last' !== selectedPeriodMode ? selectedPeriodMode : 'single',
                    selected: selectedDateLabel || value,
                    onMaskValue: handleMaskPeriod,
                    placeholder: i18n[languageCode].DATE_PICKER_PLACEHOLDER,
                    className: cn(className || 'sm:tw-w-[32rem] tw-w-full', isPeriodError && 'tw-border-alert-400 tw-border-solid')
                })
            }),
            /*#__PURE__*/ jsx(PopperContent, {
                className: "tw-w-auto",
                removePadding: true,
                children: /*#__PURE__*/ jsxs("div", {
                    className: "tw-flex tw-flex-col tw-gap-1 tw-rounded tw-p-1 tw-pt-2 tw-bg-neutral-light-100 dark:tw-bg-elevation-dark-03",
                    children: [
                        /*#__PURE__*/ jsx(Tabs, {
                            value: selectedTab,
                            onTabChange: onTabChange,
                            className: "tw-flex tw-justify-center tw-bg-transparent dark:tw-bg-transparent",
                            children: tabs.map(({ label, value })=>/*#__PURE__*/ jsx(Tab, {
                                    label: i18n[languageCode][label],
                                    className: cn(!enabledTabs.includes(value) && 'tw-hidden')
                                }, label))
                        }),
                        /*#__PURE__*/ jsx("div", {
                            className: "tw-w-full tw-h-full tw-px-2 tw-pt-1",
                            children: tabContent[selectedPeriodMode]
                        })
                    ]
                })
            })
        ]
    });
};
export { DatePickerWithTabs, tabs };
